import { Button, Classes, Intent, Tag } from '@blueprintjs/core';
import {
  StartingMaterialDatasetProviders,
  StartingMaterialPriceItem,
  findCheapestEntryInLowestTier,
  findCheapestEntryOverall,
} from '@retro-ui/shared';
import cx from 'classnames';
import { chain, isEmpty, keyBy } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  DatasetReducerDataItem,
  StartingMaterialReducer,
} from '@f/services/starting-material/starting-material.types';

import { PriceEntry } from './PriceEntry';
import PriceList from './PriceList';
import cls from './Pricing.module.scss';
import { SuppliersLink } from './suppliers-link/SuppliersLink';

const DATASET_SELECTION_NONE = 'none';
const DATASET_SELECTION_COMPLETE = 'complete';
const DATASET_SELECTION_PARTIAL = 'partial';

function buildEntries(
  data: StartingMaterialPriceItem[],
  datasetMap: { [extendedId: string]: DatasetReducerDataItem },
  selectedDatasets: SelectedDataset[]
) {
  if (isEmpty(datasetMap)) {
    return [];
  }

  return chain(data)
    .groupBy('datasetId')
    .map((entries, datasetId) => {
      if (!datasetMap[datasetId]) {
        return undefined;
      }

      return {
        datasetId,
        datasetName: datasetMap[datasetId].name,
        entries,
        cheapestEntryOverall: findCheapestEntryOverall(entries),
        cheapestEntryInLowestTier: findCheapestEntryInLowestTier(entries),
        createdAt: datasetMap[datasetId].createdAt,
        provider: datasetMap[datasetId].provider,
      };
    })
    .compact()
    .map(datasetEntries => {
      const { datasetId } = datasetEntries;

      const selectedDataset = selectedDatasets.find(dataset => dataset.id === datasetId);

      if (!selectedDataset) {
        return { ...datasetEntries, selection: DATASET_SELECTION_NONE };
      }

      if (Array.isArray(selectedDataset.selected_suppliers)) {
        return { ...datasetEntries, selection: DATASET_SELECTION_PARTIAL };
      }

      return { ...datasetEntries, selection: DATASET_SELECTION_COMPLETE };
    })
    .value();
}

type SelectedDataset = {
  id: string;
  selected_suppliers?: string[];
};

interface PricingProps {
  priceItems?: StartingMaterialPriceItem[];
  selectedDatasets?: SelectedDataset[];
}

function Pricing(props: PricingProps) {
  const { priceItems = [], selectedDatasets = [] } = props;

  const datasets = useSelector((state: any) => {
    const startingMaterial = state.startingMaterial as StartingMaterialReducer;
    const datasets = Object.values(startingMaterial.dataset.data);
    return keyBy(datasets, 'extendedId');
  });

  const [viewMap, setViewMap] = React.useState({});

  const datasetsWithEntries = React.useMemo(
    () => buildEntries(priceItems, datasets, selectedDatasets),
    [priceItems, datasets, selectedDatasets]
  );

  const toggleView = (datasetId: string) => () => {
    setViewMap({
      ...viewMap,
      [datasetId]: !viewMap[datasetId],
    });
  };

  return (
    <ul className={cls.datasetList}>
      {datasetsWithEntries.map(dataset => {
        const isSinglePrice = dataset.entries.length === 1;
        const isDatasetSelected = dataset.selection !== DATASET_SELECTION_NONE;
        const isProviderCas = dataset.provider === StartingMaterialDatasetProviders.Cas;

        return (
          <li key={dataset.datasetName}>
            <div className={cls.dataset}>
              <div className={cls.datasetEntriesButtonContainer}>
                <Button
                  className={cls.datasetEntriesButton}
                  outlined
                  intent={Intent.PRIMARY}
                  icon={viewMap[dataset.datasetId] ? 'chevron-down' : 'chevron-right'}
                  onClick={toggleView(dataset.datasetId)}
                  disabled={isProviderCas || dataset.entries.length < 1}
                />
              </div>
              <div className={cls.datasetHeading}>
                <div className={cls.datasetHeadingMetadata}>
                  <h3 className={cx(Classes.HEADING, cls.datasetName)}>{dataset.datasetName}</h3>
                  {dataset.createdAt && (
                    <span className={cls.datasetDate}>
                      Current as of {dataset.createdAt.toLocaleDateString()}
                    </span>
                  )}
                </div>
                {dataset?.provider && (
                  <SuppliersLink
                    compound={dataset.cheapestEntryOverall}
                    provider={dataset.provider}
                  />
                )}
                {isDatasetSelected && (
                  <Tag large minimal round intent={Intent.SUCCESS} icon="tick">
                    {dataset.selection === DATASET_SELECTION_COMPLETE
                      ? 'Selected'
                      : 'Partially selected'}
                  </Tag>
                )}
              </div>
              <div className={cls.datasetEntries}>
                {!viewMap[dataset.datasetId] && (
                  <div className={cls.entries}>
                    {!isSinglePrice && (
                      <>
                        <PriceEntry
                          entry={dataset.cheapestEntryOverall}
                          title="Cheapest"
                          icon="bank-account"
                        />
                        <PriceEntry
                          entry={dataset.cheapestEntryInLowestTier}
                          title="Shortest shipping time"
                          icon="lightning"
                        />
                      </>
                    )}

                    {isSinglePrice && !isProviderCas && (
                      <PriceEntry
                        entry={dataset.cheapestEntryOverall}
                        title="Single price"
                        icon="shopping-cart"
                      />
                    )}

                    {isSinglePrice && isProviderCas && (
                      <PriceEntry
                        entry={dataset.cheapestEntryOverall}
                        title="Cheapest"
                        icon="bank-account"
                      />
                    )}
                  </div>
                )}
                {viewMap[dataset.datasetId] && (
                  <>
                    <PriceList data={dataset.entries} selectedDatasets={selectedDatasets} />
                    {dataset.entries.length > 20 && (
                      <div>
                        <Button
                          outlined
                          intent={Intent.PRIMARY}
                          text="Hide prices"
                          icon="chevron-up"
                          onClick={toggleView(dataset.datasetId)}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default React.memo(Pricing);
